import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';

import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import Formatted from '@flipster/react/design-utils/formatted';
import { FormattedNumberInput } from '@flipster/react/design-utils/formatted-input';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { DefaultNumberInputMaxLength, SelectBox } from '@flipster/ui';
import Num, { inputFormatter, numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

import ExchangeDesc from './ExchangeDesc';
import RatioButtons from './RatioButtons';
import { mintBunja, mintFormBunja, mintPreviewBunja } from './state';

interface Props {
  onClose: () => void;
  onPreview: () => void;
}

const Form = ({ onClose, onPreview }: Props) => {
  const t = useTranslations();
  const { availableBalanceAtom, flowTypeAtom, collateralCurrenciesAtom, sortedCollateralCurrenciesAtom } =
    useBunja(mintBunja);
  const { fromCurrencyAtom, toCurrencyAtom, fromAmountAtom, toAmountAtom, errorMessageAtom, minAmountAtom } =
    useBunja(mintFormBunja);
  const { postMintPreview, isPreviewLoadingAtom } = useBunja(mintPreviewBunja);

  const [fromCurrency, setFromCurrency] = useAtom(fromCurrencyAtom);
  const [toCurrency, setToCurrency] = useAtom(toCurrencyAtom);
  const [fromAmount, setFromAmount] = useAtom(fromAmountAtom);

  const isPreviewLoading = useAtomValue(isPreviewLoadingAtom);
  const availableBalance = useAtomValue(availableBalanceAtom);
  const flowType = useAtomValue(flowTypeAtom);
  const sortedCollateralCurrencies = useAtomValue(sortedCollateralCurrenciesAtom);
  const originalCollateralCurrencies = useAtomValue(collateralCurrenciesAtom);

  const isMintFlow = flowType === 'MINT';
  const isRedeemFlow = flowType === 'REDEEM';
  // Use sorted currencies (by balance) for MINT flow, original order for REDEEM flow
  const collateralCurrencies = isMintFlow ? sortedCollateralCurrencies : originalCollateralCurrencies;
  const toAmount = useAtomValue(toAmountAtom);
  const errorMessage = useAtomValue(errorMessageAtom);
  const minAmount = useAtomValue(minAmountAtom);

  const isInvalid = !!errorMessage || !fromAmount || !toAmount;

  const availableCurrenciesItems = useMemo(
    () =>
      collateralCurrencies.map((currency) => ({
        value: currency.currency,
        label: (
          <CurrencyDisplay
            currency={currency.currency}
            icon={<CurrencyDisplay.Icon size={24} space={8} />}
            mainLabel={
              <CurrencyDisplay.Label
                value='shortName'
                css={{ textStyle: 'Text/L/Strong', textAlign: 'center', color: 'content.default.level-5' }}
              />
            }
          />
        ),
      })),
    [collateralCurrencies],
  );

  const updateFromAmountByRatio = (ratio: number) => {
    const value = Num(availableBalance).dividedBy(100).multipliedBy(ratio).toString();
    const fromAmount = inputFormatter(value, {
      withComma: true,
      maxLength: DefaultNumberInputMaxLength,
    }).replace(/,/g, '');
    setFromAmount(fromAmount);
  };

  const handleChangeCurrency = (currency: string) => {
    setFromAmount('');
    if (isMintFlow) {
      setFromCurrency(currency);
    } else {
      setToCurrency(currency);
    }
  };

  const handlePreview = async () => {
    segment.track(EarnEvent.EarnPreviewClick, {
      manage_type: flowType,
      from_currency: fromCurrency,
      to_currency: toCurrency,
      from_amount: fromAmount,
      to_amount: toAmount,
      available_balance: availableBalance,
    });
    await postMintPreview({ onSuccess: () => onPreview() });
  };

  // Set default currency only when not already selected (to prevent resetting on wallet balance updates)
  useEffect(() => {
    if (collateralCurrencies.length > 0) {
      if (isMintFlow && !fromCurrency) {
        setFromCurrency(collateralCurrencies[0].currency);
      } else if (!isMintFlow && !toCurrency) {
        setToCurrency(collateralCurrencies[0].currency);
      }
    }
  }, [collateralCurrencies, isMintFlow, fromCurrency, toCurrency, setFromCurrency, setToCurrency]);

  return (
    <>
      <Modal.Header>
        <Modal.Title label={t(isMintFlow ? 'Mint.r251008.Mint' : 'Redeem.r251008.Redeem')} />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 32 })}>
          <div>
            <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
              {t('Common.r251008.Available_balance')}
            </p>
            <div
              className={css({
                pt: 12,
                pb: 16,
                borderBottomWidth: '1px',
                borderBottomStyle: 'solid',
                borderColor: 'border.default.level-1',
              })}
            >
              <Formatted.Number
                value={availableBalance}
                suffix={fromCurrency}
                css={{ textStyle: 'Title/M/Monospace', color: 'content.default.level-5' }}
              />
            </div>
          </div>
          <div className={stack({ gap: 12 })}>
            <div
              className={hstack({
                justify: 'space-between',
                textStyle: 'Text/L/Regular',
                color: 'content.default.level-3',
              })}
            >
              <p>{t('Common.r251008.From')}</p>
              <p className={css({ textAlign: 'right' })}>{t('Common.r251008.To')}</p>
            </div>
            {isMintFlow && (
              <div className={hstack({ gap: 12 })}>
                <div key='selector' className={css({ flex: 1, maxW: { tablet: 186 }, h: 56 })}>
                  <SelectBox
                    title={t('Select_asset.r250401.Select_asset')}
                    shape='line'
                    listTheme='line'
                    value={fromCurrency}
                    className={css({
                      w: '100%',
                      h: 56,
                      justifyContent: 'center',
                      // TODO: [sh] 구버전 디자인인 Conversion의 SelectBox를 그대로 가져와서 변경된 디자인이라 억지로 끼워맞춰야 함
                      '& > button': { backgroundColor: 'transparent!', justifyContent: 'center!', h: '56px!' },
                      '& > button > span': { display: 'flex', alignItems: 'center' },
                    })}
                    items={availableCurrenciesItems}
                    onSelect={(value) => handleChangeCurrency(value!)}
                  />
                </div>
                <ArrowRightIcon key='arrow' size={20} />
                <div key='currency-display' className={css(BoxStyle)}>
                  <CurrencyDisplay
                    css={{ w: '100%', justifyContent: 'center' }}
                    currency={toCurrency}
                    icon={<CurrencyDisplay.Icon size={24} space={8} />}
                    mainLabel={<CurrencyDisplay.Label value='shortName' css={{}} />}
                  />
                </div>
              </div>
            )}
            {isRedeemFlow && (
              <div className={hstack({ gap: 12 })}>
                <div key='currency-display' className={css(BoxStyle)}>
                  <CurrencyDisplay
                    css={{ w: '100%', justifyContent: 'center' }}
                    currency={fromCurrency}
                    icon={<CurrencyDisplay.Icon size={24} space={8} />}
                    mainLabel={<CurrencyDisplay.Label value='shortName' css={{}} />}
                  />
                </div>
                <ArrowRightIcon key='arrow' size={20} />
                <div key='selector' className={css({ flex: 1, maxW: { tablet: 186 }, h: 56 })}>
                  <SelectBox
                    title={t('Select_asset.r250401.Select_asset')}
                    shape='line'
                    listTheme='line'
                    value={toCurrency}
                    className={css({
                      w: '100%',
                      h: 56,
                      justifyContent: 'center',
                      // TODO: [sh] 구버전 디자인인 Conversion의 SelectBox를 그대로 가져와서 변경된 디자인이라 억지로 끼워맞춰야 함
                      '& > button': { backgroundColor: 'transparent!', justifyContent: 'center!', h: '56!' },
                      '& > button > span': { display: 'flex', alignItems: 'center' },
                    })}
                    items={availableCurrenciesItems}
                    onSelect={(value) => handleChangeCurrency(value!)}
                  />
                </div>
              </div>
            )}
          </div>
          <div className={stack({ gap: 12 })}>
            <p
              className={hstack({
                justify: 'space-between',
                textStyle: 'Text/L/Regular',
                color: 'content.default.level-3',
              })}
            >
              {t('Convert.r250401.Amount')}
            </p>
            <div className={hstack({ gap: 12 })}>
              <FormattedNumberInput
                inputMode='decimal'
                className={css(BoxStyle)}
                placeholder={t('Convert.r250401.At_least_amount', {
                  amount: numberFormatter(minAmount, { withComma: true, toFixed: !Num(minAmount).isInteger() }),
                })}
                value={fromAmount}
                onChange={(value) => setFromAmount(value)}
              />
              <ArrowRightIcon size={20} />
              <Formatted.Number
                value={toAmount}
                css={{
                  ...BoxStyle,
                  borderColor: 'border.interaction.disabled',
                }}
              />
            </div>
            <RatioButtons onClick={updateFromAmountByRatio} />
            <ExchangeDesc />
          </div>
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.state.negative', textAlign: 'center' })}>
            {errorMessage}
          </p>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          label={t('Common.r250401.Cancel')}
          color='neutral'
          hierarchy='secondary'
          size='large'
          onClick={onClose}
        />
        <Button
          label={t('Common.r250710.Preview')}
          color='accent'
          hierarchy='primary'
          size='large'
          withSpinner
          isLoading={isPreviewLoading}
          disabled={isInvalid}
          onClick={handlePreview}
        />
      </Modal.Footer>
    </>
  );
};

export default Form;

const BoxStyle = css.raw({
  flex: 1,
  h: 56,
  maxW: { tablet: 186 },
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default.level-3',
  rounded: 4,
  p: 16,
  textStyle: 'Text/L/Strong',
  textAlign: 'center',
  color: 'content.default.level-5',
});
